import { useEffect, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { TruncatedText } from "@/components/ui/truncated-text";
import { cn } from "@/lib/utils";

export interface KeywordComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Suggestions for what has been typed so far. Resolved by the caller, not filtered here. */
  suggestions: string[];
  /** Fired on Enter or when a suggestion is chosen. */
  onSubmit: (value: string) => void;
  placeholder?: string;
  /** When provided, a clear (X) button shows on the right while there is a value. */
  onClear?: () => void;
  id?: string;
  className?: string;
}

/**
 * AUT-429. The keyword type-ahead: an Input with a suggestion list in a Popover. Type to see
 * matches, arrow keys to move, Enter to commit either the highlighted suggestion or the raw text.
 *
 * Ported from the design reference's `KeywordCombobox`, with **one deliberate difference**: the
 * reference takes `options: string[]` -- every keyword in the universe -- and filters it here with
 * `Array.filter`. A real brand carries 1k-10k keywords per product across several products, so that
 * array cannot be shipped to the browser. This takes already-resolved `suggestions` instead and
 * leaves the matching to the server. The interaction is identical; only the data source moved.
 */
export function KeywordCombobox({
  value,
  onValueChange,
  suggestions,
  onSubmit,
  placeholder = "Search keywords…",
  onClear,
  id,
  className,
}: KeywordComboboxProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  // Suggestions arrive asynchronously, so the highlight can outlive the list it pointed into --
  // Enter would then commit whatever now sits at that index, which is not what the user saw.
  //
  // Keyed on the **contents**, not the array. A refetch that returns the same list still returns a
  // new array, and resetting on identity dropped the highlight a moment after ArrowDown set it --
  // Enter then committed the raw text. That is invisible in a unit test, where the list only
  // changes when the test changes it; it took the browser run to surface.
  const identity = suggestions.join("\u0000");
  useEffect(() => setHighlight(-1), [identity]);

  const commit = (term: string) => {
    onValueChange(term);
    setOpen(false);
    setHighlight(-1);
    onSubmit(term);
  };

  return (
    <Popover open={open && suggestions.length > 0} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            id={id}
            value={value}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={open && suggestions.length > 0}
            aria-autocomplete="list"
            className={cn("h-9 rounded-lg bg-card pl-9", onClear && value ? "pr-9" : undefined)}
            onChange={(e) => {
              onValueChange(e.target.value);
              setHighlight(-1);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
                setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, -1));
              } else if (e.key === "Enter") {
                // The form around this would otherwise submit the raw text while a suggestion is
                // highlighted, so this owns Enter in both cases.
                e.preventDefault();
                commit(highlight >= 0 ? suggestions[highlight] : value);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
          />
          {onClear && value && (
            <button
              type="button"
              aria-label="Clear keyword"
              onClick={() => {
                setOpen(false);
                setHighlight(-1);
                onClear();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-[260px] p-1"
        // Without this the list steals focus from the input on open and typing stops.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // And this is the other half of that decision. `PopoverContent` is a dismissable layer that
        // closes when focus sits outside it -- which, because the line above deliberately leaves
        // focus in the input, is always. Clicking into a field that already held a keyword opened
        // the list and closed it again ~170ms later, with no blur and no outside click: a visible
        // flash of suggestions the user did not ask to dismiss. Reported from dev 2026-09-25.
        //
        // Closing is not lost, it is just no longer Radix's job: `onBlur` closes on the way out,
        // Escape closes, and committing a suggestion closes. All four are covered by tests.
        onFocusOutside={(e) => e.preventDefault()}
        // A blur closes the list, so a click must not first move focus away from the input.
        onMouseDown={(e) => e.preventDefault()}
      >
        <ul className="max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(s)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                  i === highlight ? "bg-primary/15 text-foreground" : "text-foreground hover:bg-muted",
                )}
              >
                {/* A long-tail keyword is exactly the kind that clips, and the clipped half is
                    what distinguishes it from its neighbour (AUT-237). */}
                <TruncatedText lines={1}>{s}</TruncatedText>
                {s.toLowerCase() === value.trim().toLowerCase() && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                )}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
